import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";
import React, { useCallback } from "react";
import { useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import CalendarIcon from "@material-ui/icons/CalendarToday";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import HorseListsModal from "components/HorseListsModal";
import Link from "components/Link";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import ICalendarDay from "interfaces/ICalendarDay";
import { SortOrder } from "interfaces/SortOrder";
import WorkoutsPageState from "interfaces/WorkoutsPageState";
import {
  currentTimeZoneString,
  getCalendar,
  shouldShutOffDate
} from "utils/date-utils";
import { WORKOUT_REQUEST_GET_OPTIONS } from "../../options";
import AvailableDate from "./AvailableDate";
import DisabledDate from "./DisabledDate";
import ScheduledDate from "./ScheduledDate";

import useStyles from "./styles";

export interface Props {
  horse: racehorse360.IHorse;
  selectedFacility: racehorse360.IFacility;
  facilityBlackoutsCalendar?: ICalendarDay[];
}

const DAYS_PER_SCREEN: number = 35;

const getSchedule = (
  workoutRequests: racehorse360.IWorkoutRequest[],
  facilityBlackouts: ICalendarDay[]
) => {
  const schedule = getCalendar(startOfToday(), DAYS_PER_SCREEN);
  workoutRequests.forEach(workoutRequest => {
    const date = parseISO(workoutRequest.date);
    const scheduleKey = format(date, "yyyy-MM");
    if (schedule[scheduleKey]) {
      schedule[scheduleKey][date.getDate()] = workoutRequest;
    }
  });
  facilityBlackouts.forEach(calendarDay => {
    const scheduleKey = format(calendarDay.date, "yyyy-MM");
    if (schedule[scheduleKey]) {
      const dayKey = calendarDay.date.getDate();
      schedule[scheduleKey][dayKey] = {
        ...schedule[scheduleKey][dayKey],
        ...calendarDay,
        date: schedule[scheduleKey][dayKey].date
      };
    }
  });
  return Object.values(schedule).map(month => Object.values(month));
};

const CondensedViewCalendar = (props: Props) => {
  const { horse, selectedFacility, facilityBlackoutsCalendar } = props;
  const classes = useStyles();
  const queryClient = useQueryClient();
  const scheduledDatesOnly = useSelector(
    (state: { workoutsPage: WorkoutsPageState }) =>
      state?.workoutsPage?.scheduledDatesOnly
  );

  const { useListWorkoutRequests } = useRacehorse360Api();
  const listWorkoutRequestsQueryKey = `${horse.id}-list-workout-requests`;
  const hasVetLists = horse.activeVetListsCount > 0;

  const { isLoading, data } = useListWorkoutRequests(
    listWorkoutRequestsQueryKey,
    {
      query: {
        horseIds: [horse.id],
        date: {
          range: {
            relative: {
              startDate: { value: 0 },
              endDate: { value: DAYS_PER_SCREEN },
              timezone: currentTimeZoneString()
            }
          }
        },
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      pagingOptions: {
        maxResults: 37
      },
      getOptions: {
        select: WORKOUT_REQUEST_GET_OPTIONS,
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );
  const schedule = getSchedule(
    data?.workoutRequests || [],
    facilityBlackoutsCalendar
  );

  const handleDateUpdate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [listWorkoutRequestsQueryKey]
    });
  }, [listWorkoutRequestsQueryKey]);

  if (!horse) {
    return <Box className={classes.root} />;
  }

  const renderDate = (workoutRequest, mi, di) => {
    const isShutOffDate = shouldShutOffDate(
      parseISO(workoutRequest.date),
      selectedFacility
    );

    if (workoutRequest.id && workoutRequest.facility) {
      return (
        <ScheduledDate
          key={workoutRequest.date}
          className={classes.calendarDate}
          workoutRequestData={workoutRequest}
          horse={horse}
          onUpdate={handleDateUpdate}
          isToday={mi === 0 && di === 0}
        />
      );
    }

    if (workoutRequest.facilityBlackout || isShutOffDate) {
      return (
        <DisabledDate
          key={workoutRequest.date}
          date={parseISO(workoutRequest.date)}
          isToday={mi === 0 && di === 0}
        />
      );
    }

    return (
      <AvailableDate
        key={workoutRequest.date}
        date={workoutRequest.date}
        selectedFacilityId={selectedFacility.id}
        horseId={horse.id}
        isToday={mi === 0 && di === 0}
        onUpdate={handleDateUpdate}
      />
    );
  };

  return (
    <Box
      className={clsx(classes.root, {
        [classes.rootScheduledOnly]: scheduledDatesOnly
      })}
    >
      <Link
        to={routes.horseWorkouts.path.replace(":horseId", String(horse.id))}
        className={classes.nameWrapper}
      >
        <Typography
          className={clsx(classes.name, {
            [classes.alert]: hasVetLists
          })}
        >
          {horse.name}
        </Typography>
        {isLoading ? (
          <Loader size={14} className={classes.loader} />
        ) : (
          <>
            <Typography className={classes.count}>
              &nbsp;
              {Boolean(data?.workoutRequests?.length) &&
                `(${data.workoutRequests.length})`}
            </Typography>
            {hasVetLists && (
              <HorseListsModal horseId={horse.id} horseName={horse.name} />
            )}
          </>
        )}
        <CalendarIcon className={classes.detailsLink} />
      </Link>
      <Box
        className={clsx(classes.calendar, {
          [classes.calendarScheduledOnly]: scheduledDatesOnly
        })}
      >
        {isLoading && <Box className={classes.fog} />}
        {scheduledDatesOnly ? (
          <Box className={clsx(classes.calendarMonth, classes.scheduledOnly)}>
            {data?.workoutRequests.map(workoutRequest => (
              <ScheduledDate
                className={classes.widePane}
                key={workoutRequest.date}
                workoutRequestData={workoutRequest}
                horse={horse}
                onUpdate={handleDateUpdate}
                isWide
                isToday={isToday(parseISO(workoutRequest.date))}
              />
            ))}
          </Box>
        ) : (
          schedule.map((month, mi) => (
            <Box key={month[0].date} className={classes.calendarMonth}>
              {month.map(
                (
                  workoutRequest: racehorse360.IWorkoutRequest & ICalendarDay,
                  di
                ) => renderDate(workoutRequest, mi, di)
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default React.memo(CondensedViewCalendar, (prev, next) => {
  return (
    prev.horse.id === next.horse.id &&
    prev.selectedFacility.id === next.selectedFacility.id
  );
});
